import { Button, CustomSearch, DisplayItemsPerPageSelect, Paginator } from 'components';
import { AdminList } from '../AdminList';
import { StyledAdminEditEquipmentGroups } from './styles';
import { DataExportButton } from '../DataExportButton';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { RefreshButton } from '../RefreshButton';
import { RootState, useAppDispatch, useAppSelector } from 'store/store';
import { useSelector } from 'react-redux';
import {
  equipmentGroupsListSetPage,
  selectAdminEquipmentGroups,
  setDailyHashrateFilter,
  setHourlyHashrateFilter,
  setItemsPerEquipmentGroupsListPage,
  setSearch,
  setSort,
  setTestHashrateFilter,
  setWeeklyHashrateFilter,
} from 'store/slices/adminEquipmentGroupsSlice';
import {
  IAdminEquipmentGroup,
  IAdminDeviceGroup,
  TNormalizedDeviceGroups,
  TSortAdminEquipmentGroupByHeaderTitle,
} from 'types';
import {
  fetchEquipmentGroups,
  fetchExportEquipmentGroups,
} from 'store/thunks/adminEquipmentGroupsThunk';
import { AdminEquipmentGroupsListHeader } from '../AdminEquipmentGroupsListHeader';
import { downloadCsvFile, getNextSortDir, parseHashrateValueRange } from 'utils';
import { AdminEquipmentGroupsListRow } from '../AdminEquipmentGroupsListRow';
import { AdminGroupsSelect } from '../AdminGroupsSelect';
import { selectAdminDeviceGroups } from 'store/slices/adminDeviceGroupsSlice';
import { AdminRepository } from 'repositories/AdminRepository';
import { EApiResponseStatus } from 'types/api';
import { VALIDATION_ERROR_ADMIN_GROUP_ID_REQUIRED } from 'constants/errorsConstants';
import { AdminConfirmModal } from '../AdminConfirmModal';

interface IAdminEditEquipmentGroupsProps {
  children?: React.ReactNode;
}

export const AdminEditEquipmentGroups = ({ children }: IAdminEditEquipmentGroupsProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { groups } = useSelector(selectAdminEquipmentGroups);
  const { normalizedGroups } = useSelector(selectAdminDeviceGroups);
  const { token } = useAppSelector((state) => state.auth);
  const [showModal, setShowModal] = useState(false);
  const [selectedAll, setSelectedAll] = useState(false);
  const [selectedEquipmentGroupsIds, setSelectedEquipmentGroupsIds] = useState<
    IAdminEquipmentGroup['workerId'][]
  >([]);
  const [selectedDeviceGroup, setSelectedDeviceGroup] = useState<IAdminDeviceGroup['id'] | null>(
    null,
  );
  const { pagesQuantity, currentPage, itemsPerPage, sortBy, sortDir } = useSelector(
    (state: RootState) => state.adminEquipmentGroups,
  );

  const setSortHandler = (sort: {
    sortBy: keyof IAdminEquipmentGroup;
    sortDir: 'ASC' | 'DESC' | 'NONE';
  }) => {
    dispatch(
      setSort({
        sortBy: sort.sortDir === 'NONE' ? null : sort.sortBy,
        sortDir: sort.sortDir === 'NONE' ? null : sort.sortDir,
      }),
    );
    dispatch(fetchEquipmentGroups());
  };

  const sortHandler = (nextSortBy: TSortAdminEquipmentGroupByHeaderTitle) => {
    const nextSortDir = getNextSortDir(sortBy, nextSortBy, sortDir as 'ASC' | 'DESC' | 'NONE');
    setSortHandler({ sortBy: nextSortBy, sortDir: nextSortDir });
  };

  const filterHandler = (filterByValue: {
    filterBy: keyof IAdminEquipmentGroup;
    filterValue: string;
  }) => {
    const { filterBy, filterValue } = filterByValue;

    switch (filterBy) {
      case 'hashRate1hour':
        dispatch(setHourlyHashrateFilter(parseHashrateValueRange(filterValue)));
        break;
      case 'hashRate24h':
        dispatch(setDailyHashrateFilter(parseHashrateValueRange(filterValue)));
        break;
      case 'hashrateWeekly':
        dispatch(setWeeklyHashrateFilter(parseHashrateValueRange(filterValue)));
        break;
      case 'testHashrate':
        dispatch(setTestHashrateFilter(parseHashrateValueRange(filterValue)));
        break;
      default:
        return;
    }

    dispatch(fetchEquipmentGroups());
  };

  const changeCurrentPageHandler = (currentPage: number) => {
    dispatch(equipmentGroupsListSetPage(currentPage));
    dispatch(fetchEquipmentGroups());
  };

  const changeItemsPerPageQuantityHandler = (itemsQuantity: number) => {
    dispatch(setItemsPerEquipmentGroupsListPage(itemsQuantity));
    dispatch(fetchEquipmentGroups());
  };

  const setSearchFilterHandler = (value: string) => {
    dispatch(setSearch(value === '' ? null : value));
    dispatch(fetchEquipmentGroups());
  };

  const refreshButtonHandler = () => {
    dispatch(fetchEquipmentGroups());
  };

  const dataExportButtonHandler = async () => {
    const data = await dispatch(fetchExportEquipmentGroups()).unwrap();
    downloadCsvFile(data.exportData, 'Equipment groups');
  };

  const selectAllHandler = (isSelectedAll: boolean) => {
    if (isSelectedAll) {
      const allIds = groups.map((el) => el.workerId);
      setSelectedEquipmentGroupsIds(allIds);
      setSelectedAll(true);
    } else {
      setSelectedEquipmentGroupsIds([]);
      setSelectedAll(false);
    }
  };

  const selectHanler = (id: IAdminEquipmentGroup['workerId'], selected: boolean) => {
    if (selected) {
      setSelectedEquipmentGroupsIds((prev) => [...prev, id]);
    } else {
      const updatedSelectedElementsIds = selectedEquipmentGroupsIds.filter(
        (selectedId) => selectedId !== id,
      );
      setSelectedEquipmentGroupsIds(updatedSelectedElementsIds);
    }
  };

  const selectGroupHandler = (groupId: IAdminDeviceGroup['id'] | null) => {
    setSelectedDeviceGroup(groupId);
  };

  const submitEditCurrentDeviceGroupHandler = () => {
    setShowModal(true);
  };

  const submitAction = () => {
    submitEditCurrentDeviceGroup();
    setShowModal(false);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const submitEditCurrentDeviceGroup = async () => {
    if (selectedEquipmentGroupsIds.length === 0) return;

    try {
      if (!token) throw Error('no token or unathorised');
      if (typeof selectedDeviceGroup !== 'number')
        throw Error(VALIDATION_ERROR_ADMIN_GROUP_ID_REQUIRED);

      const requests = selectedEquipmentGroupsIds
        .map((id) => {
          const group = groups.find((group) => group.workerId === id);
          if (!group) throw new Error(`Worker with ID ${id} not found`);
          return group;
        })
        .filter((group) => Number(group.currentDeviceGroupId) !== selectedDeviceGroup)
        .map((group) =>
          AdminRepository.setEquipmentGroupDeviceGroupName(token, {
            workerId: group.workerId,
            groupId: selectedDeviceGroup,
          }),
        );

      if (requests.length === 0) return;

      const responses = await Promise.all(requests);

      const failedResponse = responses.find((res) => res.status === EApiResponseStatus.FAILURE);
      if (failedResponse) throw new Error(failedResponse.error);

      await dispatch(fetchEquipmentGroups());
      setSelectedEquipmentGroupsIds([]);
      setSelectedAll(false);
    } catch (error) {
      console.error('submit equipment group error', error);
    }
  };

  return (
    <StyledAdminEditEquipmentGroups>
      <AdminList
        renderControls={() => (
          <div className="admin-edit-groups-controls">
            <AdminGroupsSelect
              groups={normalizedGroups}
              currentGroupId={selectedDeviceGroup}
              onSelect={selectGroupHandler}
            />
            <Button
              title={t('adminPage.editEquipmentGroups.changeGroup')}
              onClick={submitEditCurrentDeviceGroupHandler}
            />
            {showModal && (
              <AdminConfirmModal
                title={t('adminPage.confirmModal.changeGroup')}
                onConfirm={submitAction}
                onCancel={closeModal}
                show={showModal}
              />
            )}
            <div className="admin-edit-groups-controls__right">
              <CustomSearch onChange={setSearchFilterHandler} />
              <DataExportButton onClick={dataExportButtonHandler} />
              <RefreshButton onClick={refreshButtonHandler} />
            </div>
          </div>
        )}
      >
        <div className="admin-edit-groups-list-body">
          <AdminEquipmentGroupsListHeader
            onSelectAll={selectAllHandler}
            selectedAll={selectedAll}
            onSort={sortHandler}
            onFilter={filterHandler}
            sortBy={sortBy}
            sortDir={sortDir as 'ASC' | 'DESC' | 'NONE'}
          />
          {groups.map((group) => (
            <AdminEquipmentGroupsListRow
              key={group.workerId}
              selected={selectedEquipmentGroupsIds.includes(group.workerId)}
              onSelect={selectHanler}
              group={group}
            />
          ))}
        </div>
      </AdminList>
      {pagesQuantity > 0 && (
        <div className="list-body-pagination">
          <Paginator
            onChange={changeCurrentPageHandler}
            pageQuantity={pagesQuantity}
            currentPage={currentPage}
          />
          <DisplayItemsPerPageSelect
            onChange={changeItemsPerPageQuantityHandler}
            currentItemsPerPageQuantity={itemsPerPage}
            itemsPerPageRates={[10, 15, 25, 50]}
          />
        </div>
      )}
    </StyledAdminEditEquipmentGroups>
  );
};
